import { isAxiosError } from 'axios';
import { emptyDashboardIssues } from './dashboardEditFields.types';
import type { DashboardIssues } from './dashboardEditFields.types';

export const GENERIC_SAVE_ERROR = 'Could not save this dashboard. Please try again.';

// The dashboard field's failure shape is unusual: DRF maps `dashboard_definition` to the FULL list
// of every validation error at once, with no `detail` key. A permission failure is the other shape
// — {"detail": "..."} and no field keys at all. `request-template.ts` rejects with the raw axios
// error rather than the generated ApiError, so the body lives at error.response.data and an
// `instanceof ApiError` check would silently never match.
const COMPONENT_ERROR_PATTERN = /^components\[(\d+)\]:\s*/;

const readStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

export const resolveDashboardSaveErrors = (error: unknown): DashboardIssues => {
  const issues = emptyDashboardIssues();

  if (isAxiosError(error)) {
    const body = error.response?.data;

    if (body && typeof body === 'object') {
      const record = body as Record<string, unknown>;

      readStringList(record.dashboard_definition).forEach(message => {
        const match = COMPONENT_ERROR_PATTERN.exec(message);

        if (!match) {
          issues.general.push(message);
          return;
        }

        const index = Number(match[1]);
        const existing = issues.byComponentIndex.get(index) ?? [];
        issues.byComponentIndex.set(index, [...existing, message.replace(COMPONENT_ERROR_PATTERN, '')]);
      });

      // Everything else the serializer can reject: `name`, `visibility`, or a 403's `detail`.
      Object.entries(record).forEach(([key, value]) => {
        if (key === 'dashboard_definition') {
          return;
        }
        if (typeof value === 'string' && value.trim()) {
          issues.general.push(value);
          return;
        }
        issues.general.push(...readStringList(value));
      });
    }
  }

  if (!issues.general.length && !issues.byComponentIndex.size) {
    issues.general.push(
      error instanceof Error && error.message.trim() ? error.message : GENERIC_SAVE_ERROR
    );
  }

  return issues;
};

// The single-sentence form, for callers that surface one line rather than a per-tab list. Same body
// shape and the same fallback ladder: a `detail` sentence (the AI endpoint's 422 / 503), then the
// first string in any DRF {field: ["reason"]} array, then the transport error, then the generic.
export const resolveApiErrorMessage = (error: unknown, genericMessage: string): string => {
  if (isAxiosError(error)) {
    const body = error.response?.data;

    if (body && typeof body === 'object') {
      const { detail } = body as Record<string, unknown>;

      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }

      const firstListedError = Object.values(body as Record<string, unknown>)
        .flatMap(value => (Array.isArray(value) ? value : []))
        .find((entry): entry is string => typeof entry === 'string' && Boolean(entry.trim()));

      if (firstListedError) {
        return firstListedError;
      }
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return genericMessage;
};
